import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

const SESSION_UI_KEYS = [
  'trusttrade:lastSeenActivityAt',
  'trusttrade:lastSeenNotificationsAt',
  'trusttrade:dashboardCache',
  'trusttrade:disputesCache',
  'trusttrade:notifications',
];
const SESSION_UI_KEY_PATTERNS = ['activity', 'dispute', 'dashboard', 'notification'];

function clearSessionUiState() {
  SESSION_UI_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  [localStorage, sessionStorage].forEach((storage) => {
    Object.keys(storage).forEach((key) => {
      const lowerKey = key.toLowerCase();
      if (SESSION_UI_KEY_PATTERNS.some((pattern) => lowerKey.includes(pattern))) {
        storage.removeItem(key);
      }
    });
  });
}

function getStoredUserData() {
  try {
    return JSON.parse(localStorage.getItem('user_data') || 'null');
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  // Track if we've initialized to prevent re-runs
  const initialized = useRef(false);
  
  // Initialize state synchronously from localStorage
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user_data');
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem('session_token');
    const storedUser = localStorage.getItem('user_data');
    return !!(token && storedUser);
  });
  
  const [loading, setLoading] = useState(true);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  // Initialize ONCE on mount
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    const h = window.location.hash || '';
    if (h.includes('session_id=') || h.includes('session_token=')) {
      console.log('[AUTH] Skipping /me check - OAuth callback in progress');
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('session_token');
    console.log('[AUTH_STATE_INITIALIZED] token:', token ? 'YES' : 'NO', 'isAuthenticated:', !!(token && localStorage.getItem('user_data')));

    if (!token) {
      setLoading(false);
      return;
    }

    // Verify token and check onboarding status
    api.get('/auth/me')
      .then(response => {
        if (response.data?.user_id) {
          console.log('[TOKEN_VALID] user:', response.data.email);
          const userData = {
            user_id: response.data.user_id,
            email: response.data.email,
            name: response.data.name,
            is_admin: response.data.is_admin,
            picture: response.data.picture,
          };
          const storedUser = getStoredUserData();
          if (storedUser?.user_id && storedUser.user_id !== userData.user_id) {
            clearSessionUiState();
          }
          setUser(userData);
          setIsAuthenticated(true);
          localStorage.setItem('user_data', JSON.stringify(userData));

          // Check if onboarding is needed
          return api.get('/auth/onboarding-status');
        }
      })
      .then(res => {
        if (res?.data?.needs_onboarding) setNeedsOnboarding(true);
      })
      .catch(error => {
        console.log('[TOKEN_INVALID]', error.response?.status || error.message);
        if (error.response?.status === 401) {
          localStorage.removeItem('session_token');
          localStorage.removeItem('user_data');
          setUser(null);
          setIsAuthenticated(false);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Login function - called after successful OAuth
  const login = useCallback((userData, token) => {
    console.log('[LOGIN_CALLED] user:', userData.email);
    const storedUser = getStoredUserData();
    if (!storedUser?.user_id || storedUser.user_id !== userData.user_id) {
      clearSessionUiState();
    }

    // Store in localStorage FIRST
    localStorage.setItem('session_token', token);
    localStorage.setItem('user_data', JSON.stringify(userData));

    // Then update state
    setUser(userData);
    setIsAuthenticated(true);
    setLoading(false);

    // Check onboarding status for newly logged-in user
    api.get('/auth/onboarding-status')
      .then(res => { if (res?.data?.needs_onboarding) setNeedsOnboarding(true); })
      .catch(() => {});

    console.log('[LOGIN_COMPLETE] isAuthenticated: true');
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    console.log('[LOGOUT_CALLED]');
    
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Ignore logout API errors
    }
    
    localStorage.removeItem('session_token');
    localStorage.removeItem('user_data');
    clearSessionUiState();
    setUser(null);
    setIsAuthenticated(false);
    
    console.log('[LOGOUT_COMPLETE]');
  }, []);

  // Refresh user data from API
  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      if (response.data?.user_id) {
        const userData = {
          user_id: response.data.user_id,
          email: response.data.email,
          name: response.data.name,
          is_admin: response.data.is_admin,
          picture: response.data.picture,
        };
        const storedUser = getStoredUserData();
        if (storedUser?.user_id && storedUser.user_id !== userData.user_id) {
          clearSessionUiState();
        }
        setUser(userData);
        localStorage.setItem('user_data', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      console.error('[REFRESH_FAILED]', error);
    }
    return null;
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated,
    needsOnboarding,
    setNeedsOnboarding,
    login,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;

